import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Film,
  Loader2,
  Mic,
  Music2,
  Play,
  RefreshCw,
  Upload,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SongBasedPipelineDialog } from '@/components/SongBasedPipelineDialog';
import {
  SongBasedProductionMetadataForm,
  SongWaveformPreview,
} from '@/components/SongBasedProductionMetadataForm';
import api from '@/lib/api';
import { mediaUrlForPlayback } from '@/lib/mediaUrls';
import { getIntakeNavPrefill } from '@/lib/intakeNavPrefill';
import { pickOption, readAgentHandoff } from '@/lib/agentHandoff';
import { AgentHandoffGate } from '@/components/createPlan/AgentHandoffGate';
import {
  DEFAULT_NARRATION_STORYBOARD_STYLE,
  NARRATION_STORYBOARD_STYLES,
  storyboardStyleMeta,
} from '@/lib/narrationStoryboardStyles';
import {
  EMPTY_SONG_PRODUCTION_METADATA,
  formatSongUploadProbe,
} from '@/lib/songBasedProductionMetadata';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const IMAGE_PROVIDER_OPTIONS = [
  { value: 'nano_banana', label: 'Gemini Nano Banana' },
  { value: 'minimax', label: 'MiniMax' },
  { value: 'runway', label: 'Runway' },
  { value: 'veo3', label: 'Veo 3' },
  { value: 'inhouse', label: 'Inhouse' },
];

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '1:1', '4:3'];

const LANGUAGE_LABELS = {
  hi: 'Hindi',
  en: 'English',
  mr: 'Marathi',
  ta: 'Tamil',
  te: 'Telugu',
};

export const SongBasedFlowPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState('');
  const [storyTheme, setStoryTheme] = useState('');
  const [songUrl, setSongUrl] = useState('');
  const [songFileName, setSongFileName] = useState('');
  const [songProbe, setSongProbe] = useState(null);
  const [uploadingSong, setUploadingSong] = useState(false);
  const [language, setLanguage] = useState('hi');
  const [visualStyle, setVisualStyle] = useState(DEFAULT_NARRATION_STORYBOARD_STYLE);
  const [imageProvider, setImageProvider] = useState('nano_banana');
  const [aspectRatio, setAspectRatio] = useState('16:9');
  const [productionMeta, setProductionMeta] = useState(EMPTY_SONG_PRODUCTION_METADATA);
  const [loading, setLoading] = useState(false);
  const [job, setJob] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [progressOpen, setProgressOpen] = useState(false);
  const [userLyrics, setUserLyrics] = useState('');
  const [detectedLanguage, setDetectedLanguage] = useState('');
  const [transcribing, setTranscribing] = useState(false);
  const agentHandoff = useMemo(() => readAgentHandoff(location.state), [location.state]);
  const [handoffReady, setHandoffReady] = useState(false);
  const agentTranscribeRef = useRef('');

  useEffect(() => {
    const nav = getIntakeNavPrefill(location.state);
    if (!nav) return;
    const fromAgent = Boolean(readAgentHandoff(location.state));
    if (nav.title) setTitle(nav.title);
    if (nav.songUrl) {
      setSongUrl(nav.songUrl);
      setSongFileName(nav.audioFilename || 'intake-song');
    }
    if (nav.optionalLyrics) setUserLyrics(nav.optionalLyrics);
    if (nav.language) setLanguage(nav.language);
    if (nav.text && !nav.optionalLyrics) setStoryTheme(nav.text.slice(0, 500));
    if (nav.visualStyle) setVisualStyle(nav.visualStyle);
    if (fromAgent) {
      setImageProvider((prev) => pickOption(nav.prefill.imageProvider, IMAGE_PROVIDER_OPTIONS, prev));
      setAspectRatio((prev) => pickOption(nav.prefill.aspectRatio, ASPECT_RATIO_OPTIONS, prev));
      setHandoffReady(true);
    }
  }, [location.state]);

  const selectedStyleMeta = useMemo(() => storyboardStyleMeta(visualStyle), [visualStyle]);
  const probeSummary = useMemo(() => formatSongUploadProbe(songProbe), [songProbe]);
  const songPlaybackUrl = useMemo(() => (songUrl ? mediaUrlForPlayback(songUrl) : ''), [songUrl]);

  const transcribeSong = useCallback(async (url, lang) => {
    if (!url) return;
    setTranscribing(true);
    setUserLyrics('');
    setDetectedLanguage('');
    try {
      const { data } = await api.post('/narration/song-based/transcribe-song', {
        song_url: url,
        language: lang,
      });
      setUserLyrics(data.transcript || '');
      setDetectedLanguage(data.detected_language || '');
      if (data.transcript) {
        toast.success('Lyrics loaded — edit if needed before starting');
      } else {
        toast.warning('Transcription returned empty — check audio quality');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not transcribe song');
      setUserLyrics('');
    } finally {
      setTranscribing(false);
    }
  }, []);

  useEffect(() => {
    // The agent gave a song but no lyrics: fetch them so the handoff doesn't ask for them.
    if (!handoffReady || !songUrl || userLyrics.trim() || transcribing) return;
    if (agentTranscribeRef.current === songUrl) return;
    agentTranscribeRef.current = songUrl;
    transcribeSong(songUrl, language);
  }, [handoffReady, songUrl, userLyrics, transcribing, language, transcribeSong]);

  const handleSongUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingSong(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/narration/song-based/upload-song', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSongUrl(data.song_url || data.url);
      setSongFileName(file.name);
      setSongProbe({
        duration_seconds: data.duration_seconds,
        sample_rate_hz: data.sample_rate_hz,
        channels: data.channels,
        bitrate_kbps: data.bitrate_kbps,
        waveform_peaks: data.waveform_peaks || [],
      });
      toast.success('Song uploaded');
      await transcribeSong(data.song_url || data.url, language);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload song');
    } finally {
      setUploadingSong(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startPipeline = async () => {
    if (!title.trim()) {
      toast.error('Add a title');
      return;
    }
    if (!songUrl) {
      toast.error('Upload a song file first');
      return;
    }
    if (!userLyrics.trim()) {
      toast.error('Add or transcribe song lyrics first');
      return;
    }
    setLoading(true);
    setJob(null);
    setProgressOpen(true);
    try {
      const payload = {
        title: title.trim(),
        song_url: songUrl,
        story_theme: storyTheme.trim(),
        song_lyrics: userLyrics.trim(),
        language,
        tone: 'storytelling',
        visual_style: visualStyle,
        image_provider: imageProvider,
        image_aspect_ratio: aspectRatio,
        production_metadata: productionMeta,
      };
      const { data } = await api.post('/narration/song-based/projects', payload);
      setProjectId(data.project.id);
      setJobId(data.job_id);
      setJob({ id: data.job_id, status: 'pending', progress: 0, message: 'Queued', pipeline_trace: [] });
      toast.success('SongAI pipeline started — transcribing lyrics…');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start pipeline');
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const isRunning = job && !['completed', 'failed', 'cancelled'].includes(job.status);
  const isComplete = job?.status === 'completed';

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="song-based-flow-page">
      <AgentHandoffGate
        handoff={agentHandoff}
        ready={handoffReady}
        flowLabel="SongAI"
        busy={loading || uploadingSong || transcribing}
        onStart={startPipeline}
        fields={[
          {
            id: 'title',
            label: 'Music video title',
            valid: Boolean(title.trim()),
            render: () => <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Music video title" autoFocus />,
          },
          {
            id: 'song',
            label: 'Song file',
            valid: Boolean(songUrl),
            render: () => (
              <label className="flex items-center gap-2 rounded-md border border-input px-3 py-2 text-sm cursor-pointer hover:bg-muted/50">
                {uploadingSong ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                <span className="truncate">{songFileName || 'Choose MP3 / WAV / M4A…'}</span>
                <input type="file" accept="audio/*,.mp3,.wav,.flac,.aac,.m4a,.ogg" className="hidden" onChange={handleSongUpload} />
              </label>
            ),
          },
          {
            id: 'lyrics',
            label: 'Song lyrics',
            hint: transcribing ? 'Transcribing the song…' : 'Filled automatically from the song — edit if needed.',
            valid: Boolean(userLyrics.trim()),
            render: () => (
              <Textarea
                value={userLyrics}
                onChange={(e) => setUserLyrics(e.target.value)}
                rows={6}
                disabled={transcribing}
                placeholder="Lyrics appear here after the song is uploaded…"
              />
            ),
          },
        ]}
      />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 rounded-full px-4 py-2 mb-4 bg-white/80 border border-slate-100 shadow-sm">
          <Music2 className="w-4 h-4 text-pink-600" />
          <span className="text-sm font-semibold text-slate-600">SongAI Flow</span>
        </div>
        <h1 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight text-slate-900 mb-2">
          Song → <span className="text-pink-600">AI music video</span>
        </h1>
        <p className="text-slate-600 text-sm sm:text-base max-w-2xl mb-8">
          Upload a song, enter or confirm your lyrics, define cinematic direction, then let SongAI
          segment verses, align timestamps, detect theme, generate screenplay &amp; beat-synced visuals.
        </p>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Music video title" />
            </div>

            <div className="space-y-2">
              <Label>Upload song</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="audio/mpeg,audio/wav,audio/flac,audio/aac,audio/mp4,audio/ogg,audio/*,.mp3,.mpeg,.mpg,.mpa,.wav,.flac,.aac,.m4a,.ogg"
                className="hidden"
                onChange={handleSongUpload}
              />
              <Button
                type="button"
                variant="outline"
                className="w-full justify-start gap-2"
                disabled={uploadingSong}
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadingSong ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {songFileName || 'Choose MP3 / MPEG / WAV / FLAC / AAC / M4A / OGG'}
              </Button>
              {probeSummary && !userLyrics ? (
                <p className="text-xs text-slate-500">{probeSummary}</p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label>Story theme (optional)</Label>
              <Textarea
                value={storyTheme}
                onChange={(e) => setStoryTheme(e.target.value)}
                rows={2}
                placeholder="Leave blank — SongAI will infer a cinematic story from the lyrics"
              />
            </div>

            <SongBasedProductionMetadataForm
              metadata={productionMeta}
              onChange={setProductionMeta}
            />

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Visual style</Label>
                <Select value={visualStyle} onValueChange={setVisualStyle}>
                  <SelectTrigger><SelectValue placeholder="Choose style" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {NARRATION_STORYBOARD_STYLES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Image provider</Label>
                <Select value={imageProvider} onValueChange={setImageProvider}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {IMAGE_PROVIDER_OPTIONS.map((p) => (
                      <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Aspect ratio</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ASPECT_RATIO_OPTIONS.map((r) => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Lyrics language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="hi">Hindi</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="mr">Marathi</SelectItem>
                    <SelectItem value="ta">Tamil</SelectItem>
                    <SelectItem value="te">Telugu</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {selectedStyleMeta?.directive ? (
              <p className="text-xs text-slate-500 rounded-lg bg-slate-50 border border-slate-100 p-2">
                {selectedStyleMeta.directive}
              </p>
            ) : null}

            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                size="lg"
                className="w-full sm:flex-1 rounded-full"
                disabled={loading || isRunning || !songUrl}
                onClick={startPipeline}
              >
                {loading || isRunning ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Running pipeline…
                  </>
                ) : (
                  <>
                    <TwinneeLogo className="w-4 h-4 mr-2" />
                    Start SongAI pipeline
                  </>
                )}
              </Button>
              {projectId && jobId && (
                <Button type="button" size="lg" variant="outline" className="w-full sm:w-auto rounded-full" onClick={() => setProgressOpen(true)}>
                  View pipeline
                </Button>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm flex flex-col min-h-[420px] lg:min-h-[calc(100vh-12rem)] lg:sticky lg:top-6">
            <div className="flex items-center justify-between gap-2 mb-3">
              <h2 className="font-semibold text-slate-800 flex items-center gap-2">
                <Mic className="w-4 h-4 text-pink-600" />
                Song lyrics
              </h2>
              {songUrl && !transcribing ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs gap-1"
                  onClick={() => transcribeSong(songUrl, language)}
                >
                  <RefreshCw className="w-3 h-3" />
                  Auto-fill from audio
                </Button>
              ) : null}
            </div>

            {songPlaybackUrl ? (
              <audio
                controls
                src={songPlaybackUrl}
                className="w-full mb-4 h-9"
                preload="metadata"
              />
            ) : null}

            {songProbe?.waveform_peaks?.length ? (
              <div className="mb-4">
                <SongWaveformPreview peaks={songProbe.waveform_peaks} />
              </div>
            ) : null}

            {!songUrl && !transcribing ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-4 py-12 rounded-xl border border-dashed border-slate-200 bg-slate-50/80">
                <Music2 className="w-10 h-10 text-slate-300 mb-3" />
                <p className="text-sm text-slate-600 font-medium">Upload a song on the left</p>
                <p className="text-xs text-slate-500 mt-1 max-w-xs">
                  Lyrics will be auto-filled from audio — edit them here. These lyrics become your script and subtitles (not the raw transcript).
                </p>
              </div>
            ) : null}

            {transcribing ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 py-16">
                <Loader2 className="w-8 h-8 animate-spin text-pink-600" />
                <p className="text-sm font-medium text-slate-700">Transcribing lyrics…</p>
                <p className="text-xs text-slate-500">This may take a minute for longer songs</p>
              </div>
            ) : null}

            {songUrl && !transcribing ? (
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {detectedLanguage ? (
                    <span className="text-xs font-medium rounded-full bg-pink-50 text-pink-700 px-2.5 py-0.5 border border-pink-100">
                      Detected: {LANGUAGE_LABELS[detectedLanguage] || detectedLanguage}
                    </span>
                  ) : null}
                  {probeSummary ? (
                    <span className="text-xs text-slate-500">{probeSummary}</span>
                  ) : null}
                  {songFileName ? (
                    <span className="text-xs text-slate-500 truncate max-w-[200px]" title={songFileName}>
                      {songFileName}
                    </span>
                  ) : null}
                </div>
                <Textarea
                  value={userLyrics}
                  onChange={(e) => setUserLyrics(e.target.value)}
                  placeholder="Paste or edit your song lyrics here (verse breaks on blank lines)…"
                  className="flex-1 min-h-[240px] resize-none text-sm leading-relaxed"
                  data-testid="song-lyrics-panel"
                />
                <p className="text-xs text-slate-400 mt-2">
                  {userLyrics.split(/\s+/).filter(Boolean).length} words · used for stanzas, script &amp; subtitles
                </p>
              </div>
            ) : null}

            {songUrl && !transcribing && !userLyrics ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12">
                <p className="text-sm text-slate-600">Transcription failed or returned no text.</p>
                <Button type="button" variant="outline" size="sm" onClick={() => transcribeSong(songUrl, language)}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Try again
                </Button>
              </div>
            ) : null}
          </div>
        </div>

        {projectId && isComplete && (
          <div className="mt-8 flex flex-wrap gap-3">
            <Button variant="outline" className="rounded-full" onClick={() => navigate(`/narration/${projectId}/scenes`)}>
              <Film className="w-4 h-4 mr-2" />
              Open scenes
            </Button>
            <Button className="rounded-full" onClick={() => navigate(`/narration/${projectId}/video-editor`)}>
              <Play className="w-4 h-4 mr-2" />
              Video editor
            </Button>
          </div>
        )}
      </motion.div>

      <SongBasedPipelineDialog
        open={progressOpen}
        onOpenChange={setProgressOpen}
        jobId={jobId}
        projectId={projectId}
        onJobChange={setJob}
        onJobIdChange={setJobId}
      />
    </div>
  );
};
